import type {
  CareTeamTaskType,
  CategoryParentGoalType,
  ClinicalCareEncounterTabType,
  ClinicalCareEncounterType,
  EncounterGoalType
} from '@/shared/lib/types/clinicalCare';
import type { AccountGoalType } from '@/shared/lib/types/goal';
import type { CcmAutoEnrollmentFields } from '@/shared/lib/types/graphql';
import { gql } from '@apollo/client';

export type CreateClinicalCareEncounterResponse = {
  createClinicalCareEncounter: {
    encounterId: string;
    encounterType: string;
    message: string;
    status: string;
  };
};

export const CREATE_CLINICAL_CARE_ENCOUNTER = gql`
  mutation CreateClinicalCareEncounter($encounter: ClinicalCareEncounterInput!) {
    createClinicalCareEncounter(encounter: $encounter) {
      encounterId
      encounterType
      message
      status
    }
  }
`;

export type GetClinicalCareEncountersResponse = {
  getClinicalCareEncounters: {
    clinicalCareEncounters: ClinicalCareEncounterType[];
  };
};

export const GET_CLINICAL_CARE_ENCOUNTERS = gql`
  query GetClinicalCareEncounters($filterOptions: ClinicalCareEncountersFilterOptionsInput!) {
    getClinicalCareEncounters(filterOptions: $filterOptions) {
      clinicalCareEncounters {
        id
        startedAt
        encounterType
        encounterMode
        signOffAt
      }
    }
  }
`;

export type GetClinicalCareEncounterResponse = {
  getClinicalCareEncounter: ClinicalCareEncounterType;
};

export const GET_CLINICAL_CARE_ENCOUNTER = gql`
  query GetClinicalCareEncounter($encounterId: String!) {
    getClinicalCareEncounter(encounterId: $encounterId) {
      id
      status
      startedAt
      endedAt
      encounterType
      encounterMode
      signOffAt
      createdAt
      updatedAt
      reportS3Key
      carePlanReportS3Key
      amendable
      amendmentStatus
      lastAmendedAt
      accountsFollowupReminder {
        id
        followUpOn
        type
        mode
        appointmentRemindersEnabled
        relyingPartyAdmin {
          id
          name
          roleType
        }
      }
    }
  }
`;

export type CreatePdfClinicalCareEncounterReport = {
  createPdfClinicalCareEncounterReport: {
    pdfDownloadUrl: string;
    status: string;
  };
};

export const CREATE_PDF_CLINICAL_CARE_ENCOUNTER_REPORT = gql`
  mutation CreatePdfClinicalCareEncounterReport($clinicalCareEncounterId: ID!) {
    createPdfClinicalCareEncounterReport(clinicalCareEncounterId: $clinicalCareEncounterId) {
      status
      pdfDownloadUrl
    }
  }
`;

export type RegeneratePdfCarePlanReport = {
  regenerateCarePlanReport: {
    status: string;
    message: string;
    pdfDownloadUrl: string;
  };
};

export const REGENERATE_PDF_CARE_PLAN_REPORT = gql`
  mutation RegenerateCarePlanReport($truentityId: ID!, $rpmFollowupConsultId: String, $clinicalCareEncounterId: String) {
    regenerateCarePlanReport(
      truentityId: $truentityId
      rpmFollowupConsultId: $rpmFollowupConsultId
      clinicalCareEncounterId: $clinicalCareEncounterId
    ) {
      status
      message
      pdfDownloadUrl
    }
  }
`;

export type CreateReportCommunicationResponse = {
  createReportCommunication: {
    status: string;
    message: string;
  };
};

export const CREATE_REPORT_COMMUNICATION = gql`
  mutation CreateReportCommunication($communication: ReportCommunicationInput!) {
    createReportCommunication(communication: $communication) {
      status
      message
    }
  }
`;

export type GetLastReportCommunicationResponse = {
  getLastReportCommunication: {
    reportCommunication: {
      documentType: string;
      communicationMode: string;
      documentS3Key: string;
      metaData: JSON;
      deliveredOn: string;
    };
  };
};

export const GET_LAST_REPORT_COMMUNICATION = gql`
  query GetLastReportCommunication($encounterId: ID!) {
    getLastReportCommunication(encounterId: $encounterId) {
      reportCommunication {
        documentType
        communicationMode
        documentS3Key
        metaData
        deliveredOn
      }
    }
  }
`;

export type GetClinicalCareEncounterTabsResponse = {
  getClinicalCareEncounterTabs: {
    clinicalCareEncounterTabs: ClinicalCareEncounterTabType[];
  };
};

export const GET_CLINICAL_CARE_ENCOUNTER_TABS = gql`
  query GetClinicalCareEncounterTabs(
    $clinicalCareEncounterId: String!
    $clinicalCareEncounterTabTypes: [ClinicalCareEncounterTabTypeEnum!]!
  ) {
    getClinicalCareEncounterTabs(
      clinicalCareEncounterId: $clinicalCareEncounterId
      clinicalCareEncounterTabTypes: $clinicalCareEncounterTabTypes
    ) {
      clinicalCareEncounterTabs {
        id
        type
        status
        isManualStatusUpdate
        createdAt
        updatedAt
      }
    }
  }
`;

export type UpdateClinicalCareEncounterTabResponse = {
  updateClinicalCareEncounterTab: {
    status: string;
    message: string;
  };
};

export const UPDATE_CLINICAL_CARE_ENCOUNTER_TAB = gql`
  mutation UpdateClinicalCareEncounterTab($id: ID!, $status: String, $isManualStatusUpdate: Boolean) {
    updateClinicalCareEncounterTab(id: $id, status: $status, isManualStatusUpdate: $isManualStatusUpdate) {
      status
      message
    }
  }
`;

export type EnrollCcmOnlyPatientResponse = {
  enrollCcmOnlyPatient: {
    status: string;
    message: string;
  };
};

/** CCM enrollment without RPM, for pharmacies with CCM-only enrollment on. Leaves RPM untouched. */
export const ENROLL_CCM_ONLY_PATIENT = gql`
  mutation EnrollCcmOnlyPatient($truentityId: ID!, $reason: String) {
    enrollCcmOnlyPatient(truentityId: $truentityId, reason: $reason) {
      status
      message
    }
  }
`;

export type UpdateVisitReasonsResponse = { updateVisitReasons: { reasons: string[] } };
export type GetVisitReasonsResponse = { getVisitReasons: string[] };

/** Reasons for Visit on the Initial Visit (or an RPM follow-up). Full desired state. */
export const UPDATE_VISIT_REASONS = gql`
  mutation UpdateVisitReasons($truentityId: ID!, $reasons: [String!]!, $rpmFollowupConsultId: ID) {
    updateVisitReasons(truentityId: $truentityId, reasons: $reasons, rpmFollowupConsultId: $rpmFollowupConsultId) {
      reasons
    }
  }
`;

export const GET_VISIT_REASONS = gql`
  query GetVisitReasons($truentityId: ID!, $rpmFollowupConsultId: ID) {
    getVisitReasons(truentityId: $truentityId, rpmFollowupConsultId: $rpmFollowupConsultId)
  }
`;

export type CcmOnlyPatientState = {
  ccmOnlyPath: boolean;
  /** Former RPM patient seen in the last 12 months with an active CCM consent: not on the CCM-only path. */
  ccmOnlyPathExempt: boolean;
  /** A returning CCM-only patient whose signed Initial Visit does not qualify can start a new one. */
  canStartNewInitialVisit: boolean;
  initialVisitSignedOffAt: string | null;
};
export type GetCcmOnlyPatientStateResponse = { ccmOnlyAccountState: CcmOnlyPatientState };

/** Operation name, for refetchQueries after anything that can move a patient on or off the CCM-only path. */
export const CCM_ONLY_PATIENT_STATE_OPERATION = 'CcmOnlyPatientState';

/** The backend's answer to "is this patient on the CCM-only path". Only queried where CCM-only enrollment is on. */
export const GET_CCM_ONLY_PATIENT_STATE = gql`
  query CcmOnlyPatientState($truentityId: ID!) {
    ccmOnlyAccountState(truentityId: $truentityId) {
      ccmOnlyPath
      ccmOnlyPathExempt
      canStartNewInitialVisit
      initialVisitSignedOffAt
    }
  }
`;

export type FindingState = 'POSITIVE' | 'NEGATIVE';
export type FindingTrend = 'BETTER' | 'WORSE' | 'SAME';

/** The value a Positive asks for: a count in a range, free text, or a multi-select. */
export type ConditionFindingReveal = {
  kind: 'count' | 'text' | 'choices';
  label: string;
  min: number | null;
  max: number | null;
  choices: string[] | null;
};

/** A finding picked from choices rather than clicked Positive or Negative; none stands alone, and detailChoice asks for detail. */
export type ConditionFindingList = {
  choices: string[];
  none: string | null;
  detailChoice: string | null;
  detailLabel: string | null;
};

/** One finding; a null state means Not assessed. A scale or list finding has a value instead of a state. */
export type ConditionFinding = {
  itemKey: string;
  label: string;
  /** How the item reads when denied, e.g. "No headache". */
  negativeLabel: string;
  sideEffect: boolean;
  conditional: boolean;
  reveal: ConditionFindingReveal | null;
  scale: { min: number; max: number; trend: boolean } | null;
  list: ConditionFindingList | null;
  state: FindingState | null;
  valueNumber: number | null;
  valueText: string | null;
  valueChoices: string[] | null;
  trend: FindingTrend | null;
  /** The provider's note, on a Positive or a Negative. */
  detail: string | null;
  /** A positive red flag's recommended action. */
  clinicalResponse: string | null;
  /** The patient's response to a positive red flag, including a refusal. */
  accountResponse: string | null;
};

export type ConditionFindingSubGroup = {
  key: string;
  label: string;
  /** A Positive needs a recommended action and patient response before sign-off. */
  redFlag: boolean;
  /** Shown only while this gate is on. */
  gate: string | null;
  gateLabel: string | null;
  additionalFindings: string | null;
  findings: ConditionFinding[];
};

/** A designated condition (RPM or CCM, one per ICD-10 code), or the universal questions (diagnosisId null), with its findings. */
export type ConditionAssessment = {
  subjectKey: string;
  diagnosisId: string | null;
  diagnosisCode: string | null;
  diagnosisName: string | null;
  /** Null for a condition assessed as free text, and for the universal questions. */
  assessmentCategory: string | null;
  categoryLabel: string | null;
  catalogVersion: number | null;
  assessed: boolean;
  gates: string[];
  freeTextAssessment: string | null;
  subGroups: ConditionFindingSubGroup[];
};

export type ConditionAssessments = {
  /** False on a chart-only review, and on a visit signed off before findings existed. */
  enabled: boolean;
  signedOff: boolean;
  universal: ConditionAssessment | null;
  conditions: ConditionAssessment[];
};

/** Which visit the findings belong to: a follow-up, a clinical care encounter, or (neither) the Initial Visit. */
export type ConditionFindingsVisit = { rpmFollowupConsultId?: string; clinicalCareEncounterId?: string };

export type GetConditionAssessmentsResponse = { getConditionAssessments: ConditionAssessments };
export type SaveConditionAssessmentResponse = { saveConditionAssessment: { conditionAssessment: ConditionAssessment } | null };

const CONDITION_ASSESSMENT_FIELDS = gql`
  fragment ConditionAssessmentFields on ConditionAssessment {
    subjectKey
    diagnosisId
    diagnosisCode
    diagnosisName
    assessmentCategory
    categoryLabel
    catalogVersion
    assessed
    gates
    freeTextAssessment
    subGroups {
      key
      label
      redFlag
      gate
      gateLabel
      additionalFindings
      findings {
        itemKey
        label
        negativeLabel
        sideEffect
        conditional
        reveal {
          kind
          label
          min
          max
          choices
        }
        scale {
          min
          max
          trend
        }
        list {
          choices
          none
          detailChoice
          detailLabel
        }
        state
        valueNumber
        valueText
        valueChoices
        trend
        detail
        clinicalResponse
        accountResponse
      }
    }
  }
`;

/** A visit's universal questions and designated conditions, with their findings; no visit ID means the Initial Visit. */
export const GET_CONDITION_ASSESSMENTS = gql`
  ${CONDITION_ASSESSMENT_FIELDS}
  query GetConditionAssessments($truentityId: ID!, $rpmFollowupConsultId: ID, $clinicalCareEncounterId: ID) {
    getConditionAssessments(
      truentityId: $truentityId
      rpmFollowupConsultId: $rpmFollowupConsultId
      clinicalCareEncounterId: $clinicalCareEncounterId
    ) {
      enabled
      signedOff
      universal {
        ...ConditionAssessmentFields
      }
      conditions {
        ...ConditionAssessmentFields
      }
    }
  }
`;

/** Saves one condition's findings (or the universal questions' when diagnosisId is omitted); findings left out are Not assessed. */
export const SAVE_CONDITION_ASSESSMENT = gql`
  ${CONDITION_ASSESSMENT_FIELDS}
  mutation SaveConditionAssessment(
    $truentityId: ID!
    $rpmFollowupConsultId: ID
    $clinicalCareEncounterId: ID
    $diagnosisId: ID
    $findings: [ConditionFindingInput!]!
    $gates: [String!]
    $additionalFindings: [ConditionSubGroupTextInput!]
    $freeTextAssessment: String
  ) {
    saveConditionAssessment(
      truentityId: $truentityId
      rpmFollowupConsultId: $rpmFollowupConsultId
      clinicalCareEncounterId: $clinicalCareEncounterId
      diagnosisId: $diagnosisId
      findings: $findings
      gates: $gates
      additionalFindings: $additionalFindings
      freeTextAssessment: $freeTextAssessment
    ) {
      conditionAssessment {
        ...ConditionAssessmentFields
      }
    }
  }
`;

export type StartNewInitialVisitResponse = { startNewInitialVisit: { status: string; message: string } };

/** Archives the signed Initial Visit unchanged and opens a new one. `acknowledged` must be true. */
export const START_NEW_INITIAL_VISIT = gql`
  mutation StartNewInitialVisit($truentityId: ID!, $acknowledged: Boolean!) {
    startNewInitialVisit(truentityId: $truentityId, acknowledged: $acknowledged) {
      status
      message
    }
  }
`;

export type GetCareTeamTaskTemplatesResponse = {
  getCareTeamTaskTemplates: CareTeamTaskType[];
};

export const GET_CARE_TEAM_TASK_TEMPLATES = gql`
  query GetCareTeamTaskTemplates($goalId: ID!) {
    getCareTeamTaskTemplates(goalId: $goalId) {
      goal {
        id
      }
      templateName
      templateText
      isOnetimeTask
      active
    }
  }
`;

export type GetGoalsResponse = {
  getGoalTemplates: CategoryParentGoalType[];
};

export const GET_GOALS = gql`
  query GetGoalTemplates($medicalServiceShortNames: [MedicalServiceTypeEnum!]!, $relyingPartyId: ID) {
    getGoalTemplates(medicalServiceShortNames: $medicalServiceShortNames, relyingPartyId: $relyingPartyId) {
      category
      subCategory {
        categoryName
        goals {
          id
          goalText
          category
          subCategory
          isPublic
          smartGoalTemplates {
            id
            templateName
            templateText
            category
            subCategory
            placeholders
            validationRules
          }
          smartGoalTemplatesGrouped {
            category
            subCategory {
              categoryName
              templates {
                id
                templateName
                templateText
                placeholders
                validationRules
              }
            }
          }
        }
      }
    }
  }
`;

export type CreatePatientGoalResponse = {
  addGoalToAccount: {
    status: string;
    message: string;
  };
};

export const CREATE_PATIENT_GOAL = gql`
  mutation AddGoalToAccount($truentityId: ID!, $goalId: ID!, $patientTasks: [CustomTaskInput!]!) {
    addGoalToAccount(truentityId: $truentityId, goalId: $goalId, patientTasks: $patientTasks) {
      status
      message
    }
  }
`;

export type GetPatientGoalsResponse = {
  getAccountGoals: {
    goals: AccountGoalType[];
  };
};

export const GET_PATIENT_GOALS = gql`
  query GetAccountGoals($truentityId: ID!, $pageNum: Int, $pageSize: Int) {
    getAccountGoals(truentityId: $truentityId, pageNum: $pageNum, pageSize: $pageSize) {
      goals {
        id
        goalContent
        status
        statusNote
        goal {
          category
          subCategory
        }
        accountSmartGoals {
          id
          goalContent
        }
        careTeamTasks {
          id
          taskContent
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export type UpdateAccountGoalResponse = {
  updateAccountGoal: {
    accountGoal: {
      id: string;
      status: string;
      statusNote: string;
    };
  };
};

export const UPDATE_ACCOUNT_GOAL = gql`
  mutation UpdateAccountGoal($accountGoalId: ID!, $status: String, $statusNote: String, $sourceType: String, $sourceId: ID) {
    updateAccountGoal(
      accountGoalId: $accountGoalId
      status: $status
      statusNote: $statusNote
      sourceType: $sourceType
      sourceId: $sourceId
    ) {
      accountGoal {
        id
        status
        statusNote
      }
    }
  }
`;

export type ArchiveAccountGoalResponse = {
  archiveAccountGoal: {
    status: string;
    message: string | null;
  };
};

export const ARCHIVE_ACCOUNT_GOAL = gql`
  mutation ArchiveAccountGoal($accountGoalId: ID!) {
    archiveAccountGoal(accountGoalId: $accountGoalId) {
      status
      message
    }
  }
`;

export type GetEncounterGoalsResponse = {
  getEncounterGoals: EncounterGoalType[];
};

export const GET_ENCOUNTER_GOALS = gql`
  query GetEncounterGoals($truentityId: ID!) {
    getEncounterGoals(truentityId: $truentityId) {
      goal {
        id
        goalText
        category
        subCategory
      }
      goalContent
      isCustomized
      accountGoalId
      isSelected
      status
      patientTasks {
        templateId
        content
        isSelected
        isProviderDefault
        recordId
        userInput
        status
      }
      careTeamTasks {
        templateId
        content
        isSelected
        isProviderDefault
        recordId
        userInput
        status
      }
    }
  }
`;

export type SaveEncounterGoalsResponse = {
  saveEncounterGoals: {
    status: string;
    message: string;
  };
};

export const SAVE_ENCOUNTER_GOALS = gql`
  mutation SaveEncounterGoals($truentityId: ID!, $rpmFollowupConsultId: ID, $clinicalCareEncounterId: ID, $goals: [EncounterGoalInput!]!) {
    saveEncounterGoals(
      truentityId: $truentityId
      rpmFollowupConsultId: $rpmFollowupConsultId
      clinicalCareEncounterId: $clinicalCareEncounterId
      goals: $goals
    ) {
      status
      message
    }
  }
`;

export type CreateCustomEncounterGoalResponse = {
  createCustomEncounterGoal: {
    status: string;
    message: string;
    accountGoalId: string | null;
  };
};

export const CREATE_CUSTOM_ENCOUNTER_GOAL = gql`
  mutation CreateCustomEncounterGoal(
    $truentityId: ID!
    $goalContent: String!
    $patientTasks: [String!]
    $careTeamTasks: [String!]
    $rpmFollowupConsultId: ID
    $clinicalCareEncounterId: ID
  ) {
    createCustomEncounterGoal(
      truentityId: $truentityId
      goalContent: $goalContent
      patientTasks: $patientTasks
      careTeamTasks: $careTeamTasks
      rpmFollowupConsultId: $rpmFollowupConsultId
      clinicalCareEncounterId: $clinicalCareEncounterId
    ) {
      status
      message
      accountGoalId
    }
  }
`;

export type UpdateClinicalCareEncounterResponse = {
  updateClinicalCareEncounter: {
    message: string;
    status: string;
  } & CcmAutoEnrollmentFields;
};

export const UPDATE_CLINICAL_CARE_ENCOUNTER = gql`
  mutation UpdateClinicalCareEncounter($id: ID!, $clinicalCareEncounterInput: UpdateClinicalCareEncounterInput!) {
    updateClinicalCareEncounter(id: $id, clinicalCareEncounterInput: $clinicalCareEncounterInput) {
      message
      status
      ccmEnrolled
      ccmEnrollmentMessage
    }
  }
`;

export type ArchiveClinicalCareEncounterResponse = {
  archiveClinicalCareEncounter: {
    message: string;
    status: string;
  };
};

export const ARCHIVE_CLINICAL_CARE_ENCOUNTER = gql`
  mutation ArchiveClinicalCareEncounter($id: ID!, $sendTextReminder: Boolean, $textReminderMessage: String) {
    archiveClinicalCareEncounter(id: $id, sendTextReminder: $sendTextReminder, textReminderMessage: $textReminderMessage) {
      message
      status
    }
  }
`;

export const ClinicalCareReportTypes = {
  CARE_PLAN_REPORT: 'CARE_PLAN_REPORT',
  ENCOUNTER_REPORT: 'ENCOUNTER_REPORT',
  CCM_ENROLMENT_DETERMINATION_REPORT: 'CCM_ENROLMENT_DETERMINATION_REPORT',
  PATIENT_CARE_PLAN_REPORT: 'PATIENT_CARE_PLAN_REPORT'
} as const;

export type CreateCarePlanDocumentReportResponse = {
  createCarePlanDocumentReport: {
    status: string;
    pdfDownloadUrl: string;
    clinicalCareReportId: string;
  };
};

export const CREATE_CARE_PLAN_DOCUMENT_REPORT = gql`
  mutation CreateCarePlanDocumentReport($truentityId: ID!, $clinicalCareEncounterId: ID, $rpmFollowupConsultId: ID) {
    createCarePlanDocumentReport(
      truentityId: $truentityId
      clinicalCareEncounterId: $clinicalCareEncounterId
      rpmFollowupConsultId: $rpmFollowupConsultId
    ) {
      status
      pdfDownloadUrl
      clinicalCareReportId
    }
  }
`;

export type CreateEncounterNoteReportResponse = {
  createEncounterNoteReport: {
    status: string;
    pdfDownloadUrl: string;
    clinicalCareReportId: string;
  };
};

export type CreateCcmEnrolmentDeterminationReportResponse = {
  createCcmEnrolmentDeterminationReport: {
    status: string;
    pdfDownloadUrl: string;
    clinicalCareReportId: string;
  };
};

// clinicalCareEncounterId is required, unlike the sibling report mutations: this document only
// exists for a ClinicalCareEncounter, never for an RPM follow-up consult.
export const CREATE_CCM_ENROLMENT_DETERMINATION_REPORT = gql`
  mutation CreateCcmEnrolmentDeterminationReport($truentityId: ID!, $clinicalCareEncounterId: ID!) {
    createCcmEnrolmentDeterminationReport(truentityId: $truentityId, clinicalCareEncounterId: $clinicalCareEncounterId) {
      status
      pdfDownloadUrl
      clinicalCareReportId
    }
  }
`;

export const CREATE_ENCOUNTER_NOTE_REPORT = gql`
  mutation CreateEncounterNoteReport($truentityId: ID!, $clinicalCareEncounterId: ID, $rpmFollowupConsultId: ID) {
    createEncounterNoteReport(
      truentityId: $truentityId
      clinicalCareEncounterId: $clinicalCareEncounterId
      rpmFollowupConsultId: $rpmFollowupConsultId
    ) {
      status
      pdfDownloadUrl
      clinicalCareReportId
    }
  }
`;

export type CreatePatientCarePlanReportResponse = {
  createPatientCarePlanReport: {
    status: string;
    pdfDownloadUrl: string;
    clinicalCareReportId: string;
  };
};

export const CREATE_PATIENT_CARE_PLAN_REPORT = gql`
  mutation CreatePatientCarePlanReport($truentityId: ID!, $clinicalCareEncounterId: ID, $rpmFollowupConsultId: ID) {
    createPatientCarePlanReport(
      truentityId: $truentityId
      clinicalCareEncounterId: $clinicalCareEncounterId
      rpmFollowupConsultId: $rpmFollowupConsultId
    ) {
      status
      pdfDownloadUrl
      clinicalCareReportId
    }
  }
`;

export type ClinicalCareReportDocument = {
  id: string;
  reportType: string;
  generatedAt: string | null;
  providerName: string | null;
  reportS3Key: string | null;
  clinicalCareEncounterId: string | null;
  rpmFollowupConsultId: string | null;
};

export type GetClinicalCareReportsResponse = {
  getClinicalCareReports: ClinicalCareReportDocument[];
};

export const GET_CLINICAL_CARE_REPORTS = gql`
  query GetClinicalCareReports($truentityId: ID!, $reportType: String, $clinicalCareEncounterId: ID, $rpmFollowupConsultId: ID) {
    getClinicalCareReports(
      truentityId: $truentityId
      reportType: $reportType
      clinicalCareEncounterId: $clinicalCareEncounterId
      rpmFollowupConsultId: $rpmFollowupConsultId
    ) {
      id
      reportType
      generatedAt
      providerName
      reportS3Key
      clinicalCareEncounterId
      rpmFollowupConsultId
    }
  }
`;

export type ClinicalVisitDocumentVisitType = 'RPM_ONBOARDING' | 'RPM_FOLLOWUP' | 'CCM_ONBOARDING' | 'CCM_FOLLOWUP' | string;

export type ClinicalVisitDocument = {
  id: string;
  visitDate: string | null;
  visitType: ClinicalVisitDocumentVisitType;
  providerName: string | null;
  reportS3Key: string | null;
  source: 'CLINICAL_CARE_ENCOUNTER' | 'RPM_FOLLOWUP_CONSULT';
};

export type GetPatientClinicalVisitDocumentsResponse = {
  getPatientClinicalVisitDocuments: ClinicalVisitDocument[];
};

export const GET_PATIENT_CLINICAL_VISIT_DOCUMENTS = gql`
  query GetPatientClinicalVisitDocuments($truentityId: ID!) {
    getPatientClinicalVisitDocuments(truentityId: $truentityId) {
      id
      visitDate
      visitType
      providerName
      reportS3Key
      source
    }
  }
`;

/**
 * Composed clinical document (header + ordered content blocks).
 *
 * These types are the contract the docked SOAP note (SoapNoteContent) is built against - keep them
 * in sync with Types::ClinicalCare::Documents::ClinicalDocumentType on the backend, by hand: there
 * is no codegen, so a backend rename or field removal breaks this silently at runtime.
 */
export type DocumentBlockKind = 'DERIVED' | 'EDITABLE' | 'STATIC' | 'ATTESTATION';
export type DocumentTokenSource = 'USER_INPUT' | 'SYSTEM' | 'DERIVED';
export type DocumentTokenRenderType = 'TEXT' | 'LIST' | 'HTML';
export type ProviderInputPosition = 'BEFORE' | 'AFTER';
export type ClinicalDocumentVariant = 'INITIAL' | 'INITIAL_CCM_ONLY' | 'CLINICAL_CARE' | 'CLINICAL_CARE_ASYNC' | 'FOLLOW_UP';
export type ClinicalDocumentSection = 'SUBJECTIVE' | 'OBJECTIVE' | 'ASSESSMENT' | 'PLAN';

export type DocumentToken = {
  name: string;
  label: string | null;
  source: DocumentTokenSource;
  renderAs: DocumentTokenRenderType;
  value: string | null;
  derivedValue: string | null;
  htmlValue: string | null;
  structuredValue: unknown;
  validationRules: unknown;
  resolved: boolean;
  /**
   * False for chart and platform facts (age, sex, conditions, Sys.*), which render as read-only
   * chips. True for values a provider may enter or correct, which render as inputs.
   */
  editable: boolean;
};

export type DocumentBlock = {
  key: string;
  section: ClinicalDocumentSection | null;
  title: string;
  kind: DocumentBlockKind;
  sourceLabel: string | null;
  locked: boolean;
  content: string;
  resolvedContent: string;
  tokens: DocumentToken[];
};

export type ClinicalDocumentHeader = {
  eyebrow: string | null;
  title: string | null;
  patient: string | null;
  dob: string | null;
  age: string | null;
  dateOfService: string | null;
  provider: string | null;
  ehrId: string | null;
  visitType: string | null;
};

export type DocumentSignOffRequirement = {
  key: string;
  label: string;
  detail: string | null;
  blocking: boolean;
  satisfied: boolean | null;
};

export type ClinicalDocument = {
  documentType: string;
  variant: ClinicalDocumentVariant;
  providerInputPosition: ProviderInputPosition;
  header: ClinicalDocumentHeader;
  blocks: DocumentBlock[];
  signOffRequirements: DocumentSignOffRequirement[];
};

export type GetClinicalDocumentResponse = {
  getClinicalDocument: ClinicalDocument;
};

/** Operation name, for refetchQueries after anything the composed note is built from changes. */
export const CLINICAL_DOCUMENT_OPERATION = 'GetClinicalDocument';

export const GET_CLINICAL_DOCUMENT = gql`
  query GetClinicalDocument(
    $truentityId: ID!
    $documentType: ClinicalDocumentTypeEnum
    $clinicalCareEncounterId: ID
    $rpmFollowupConsultId: ID
    $providerInputPosition: ProviderInputPositionEnum
    $forAmendment: Boolean
  ) {
    getClinicalDocument(
      truentityId: $truentityId
      documentType: $documentType
      clinicalCareEncounterId: $clinicalCareEncounterId
      rpmFollowupConsultId: $rpmFollowupConsultId
      providerInputPosition: $providerInputPosition
      forAmendment: $forAmendment
    ) {
      documentType
      variant
      providerInputPosition
      header {
        eyebrow
        title
        patient
        dob
        age
        dateOfService
        provider
        ehrId
        visitType
      }
      blocks {
        key
        section
        title
        kind
        sourceLabel
        locked
        content
        resolvedContent
        tokens {
          name
          label
          source
          renderAs
          value
          derivedValue
          htmlValue
          structuredValue
          validationRules
          resolved
          editable
        }
      }
      signOffRequirements {
        key
        label
        detail
        blocking
        satisfied
      }
    }
  }
`;

export type SoapNoteVersionSection = {
  section: string | null;
  value: string | null;
  userInput: { name: string | null; value: string | null }[] | null;
  formatVersion: number | null;
};

export type SoapNoteVersion = {
  id: string;
  versionNumber: number;
  amendedAt: string | null;
  amendedByProviderName: string | null;
  attestationStatement: string | null;
  isCurrent: boolean;
  sections: SoapNoteVersionSection[];
};

export type GetSoapNoteVersionsResponse = {
  getSoapNoteVersions: SoapNoteVersion[] | null;
};

export const GET_SOAP_NOTE_VERSIONS = gql`
  query GetSoapNoteVersions($truentityId: ID!, $rpmFollowupConsultId: ID, $clinicalCareEncounterId: ID, $forAmendment: Boolean) {
    getSoapNoteVersions(
      truentityId: $truentityId
      rpmFollowupConsultId: $rpmFollowupConsultId
      clinicalCareEncounterId: $clinicalCareEncounterId
      forAmendment: $forAmendment
    ) {
      id
      versionNumber
      amendedAt
      amendedByProviderName
      attestationStatement
      isCurrent
      sections {
        section
        value
        userInput {
          name
          value
        }
        formatVersion
      }
    }
  }
`;

/** One row of the SOAP dialog's Readings tab: Date | Value | Type | Flag. */
export type NoteReading = {
  id: string;
  recordedAt: string;
  healthType: string;
  value: string;
  unit: string | null;
  /** VitalsHealthStatusType — 'Normal' | 'High' | 'Low' | 'Critically High' | 'Critically Low'. */
  status: string | null;
};

export type GetAccountNoteReadingsResponse = {
  getAccountNoteReadings: {
    lookbackDays: number;
    truncated: boolean;
    readings: NoteReading[];
  } | null;
};

export const GET_ACCOUNT_NOTE_READINGS = gql`
  query GetAccountNoteReadings($truentityId: ID!, $diagnosisCategory: String) {
    getAccountNoteReadings(truentityId: $truentityId, diagnosisCategory: $diagnosisCategory) {
      lookbackDays
      truncated
      readings {
        id
        recordedAt
        healthType
        value
        unit
        status
      }
    }
  }
`;
